// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The right column: the selected object's fields, and the panel-wide settings.
 *
 * Every widget writes straight into the spec through `commit`, and a blank field
 * *removes* the key rather than writing a zero — so the placeholder showing the
 * inherited default stays truthful, and the emitted YAML stays as small as what
 * the user actually chose.
 *
 * The whole column is rebuilt on every change, which is simple and fast enough;
 * `withFocusRestored` is what makes that survivable while typing.
 */

import { h, clear } from './dom.ts';
import type { PanelPreview } from '../panel/preview.ts';
import { emitYamlFragment, parseYaml, YamlError } from '../panel/yaml.ts';
import {
  ANCHORS,
  type Anchor,
  type PanelSpec,
  type Vec2,
} from '../panel/spec.ts';
import type { Selection } from './state.ts';

export type Commit = (change: (spec: PanelSpec) => void) => void;

export interface InspectorInput {
  spec: PanelSpec;
  preview: PanelPreview;
  selection: Selection | null;
  commit: Commit;
}

/**
 * Rebuild `into`, putting the caret back where it was.
 *
 * Without this, typing in a number field re-renders the panel and the cursor
 * jumps to the start after every keystroke.
 */
export function withFocusRestored(into: HTMLElement, rebuild: () => void): void {
  const active = document.activeElement as HTMLInputElement | null;
  const key = active?.dataset?.['fkey'];
  const start = active?.selectionStart ?? null;
  const end = active?.selectionEnd ?? null;

  rebuild();

  if (key === undefined) return;
  const restored = into.querySelector<HTMLInputElement>(`[data-fkey="${CSS.escape(key)}"]`);
  if (restored === null) return;
  restored.focus();
  if (start !== null && end !== null && typeof restored.setSelectionRange === 'function') {
    try {
      restored.setSelectionRange(start, end);
    } catch {
      // Some input types refuse selection ranges; focus alone is enough.
    }
  }
}

// --- field widgets ------------------------------------------------------------------

function field(label: string, control: HTMLElement): HTMLElement {
  return h('div', { class: 'field' }, h('label', {}, label), control);
}

function numberInput(
  key: string,
  value: number | undefined,
  placeholder: string,
  onChange: (v: number | undefined) => void,
  step = 'any',
): HTMLInputElement {
  const input = h('input', {
    type: 'number',
    step,
    placeholder,
    'data-fkey': key,
    value: value === undefined ? '' : String(value),
  });
  input.addEventListener('change', () => {
    const raw = input.value.trim();
    onChange(raw === '' ? undefined : Number(raw));
  });
  return input;
}

function textInput(
  key: string,
  value: string | undefined,
  placeholder: string,
  onChange: (v: string | undefined) => void,
): HTMLInputElement {
  const input = h('input', {
    type: 'text',
    placeholder,
    'data-fkey': key,
    value: value ?? '',
  });
  input.addEventListener('change', () => {
    const raw = input.value.trim();
    onChange(raw === '' ? undefined : raw);
  });
  return input;
}

function vec2Input(
  key: string,
  value: Vec2 | undefined,
  onChange: (v: Vec2 | undefined) => void,
): HTMLElement {
  const read = (): Vec2 | undefined => {
    const x = (row.children[0] as HTMLInputElement).value.trim();
    const y = (row.children[1] as HTMLInputElement).value.trim();
    if (x === '' && y === '') return undefined;
    return [Number(x || 0), Number(y || 0)];
  };
  const make = (axis: number): HTMLInputElement => {
    const input = h('input', {
      type: 'number',
      step: 'any',
      'data-fkey': `${key}.${axis}`,
      value: value === undefined ? '' : String(value[axis]),
    });
    input.addEventListener('change', () => onChange(read()));
    return input;
  };
  const row = h('div', { class: 'vec' }, make(0), make(1));
  return row;
}

function selectInput(
  key: string,
  value: string | undefined,
  options: readonly string[],
  fallback: string,
  onChange: (v: string) => void,
): HTMLSelectElement {
  const select = h('select', { 'data-fkey': key });
  for (const option of options) {
    const el = h('option', { value: option }, option);
    if ((value ?? fallback) === option) el.selected = true;
    select.append(el);
  }
  select.addEventListener('change', () => onChange(select.value));
  return select;
}

function checkboxInput(
  key: string,
  value: boolean | undefined,
  fallback: boolean,
  label: string,
  onChange: (v: boolean) => void,
): HTMLElement {
  const input = h('input', { type: 'checkbox', 'data-fkey': key });
  input.checked = value ?? fallback;
  input.addEventListener('change', () => onChange(input.checked));
  return h('label', { class: 'checkbox' }, input, h('span', {}, label));
}

function readOnly(label: string, value: string, title = value): HTMLElement {
  return field(label, h('span', { class: 'ro', title }, value));
}

function subHead(title: string, ...extra: HTMLElement[]): HTMLElement {
  return h('div', { class: 'sub-head' }, h('span', {}, title), ...extra);
}

// --- the selected object ------------------------------------------------------------

export function renderInspector(into: HTMLElement, input: InspectorInput): void {
  clear(into);
  const { spec, selection, commit } = input;

  if (selection === null) {
    into.append(h('p', { class: 'empty' }, 'nothing selected'));
    return;
  }

  into.append(h('div', { class: 'note mono' }, `${selection.kind}[${selection.index}]`));

  if (selection.kind === 'board') renderBoard(into, input);
  else if (selection.kind === 'tab') renderTab(into, input);
  else if (selection.kind === 'rail') renderRail(into, input);
  else if (selection.kind === 'text') renderText(into, input);
  else into.append(h('p', { class: 'note' }, 'patches are edited as YAML, under Settings.'));

  const problem = problemFor(input);
  if (problem !== null) into.append(h('div', { class: 'insp-error' }, problem));

  const remove = h('button', { class: 'sm danger' }, 'Delete');
  remove.addEventListener('click', () => {
    commit((s) => {
      const list = listOf(s, selection.kind);
      if (list) list.splice(selection.index, 1);
    });
  });
  into.append(h('div', { class: 'row' }, remove));
  void spec;
}

function listOf(spec: PanelSpec, kind: Selection['kind']): unknown[] | undefined {
  switch (kind) {
    case 'board': return spec.boards;
    case 'rail': return spec.rails;
    case 'patch': return spec.patches;
    case 'tab': return spec.tabs;
    case 'text': return spec.text;
  }
}

function problemFor({ preview, selection }: InspectorInput): string | null {
  if (selection === null) return null;
  const { kind, index } = selection;
  if (kind === 'board') return preview.boards[index]?.error ?? null;
  if (kind === 'rail') return preview.rails[index]?.error ?? null;
  if (kind === 'patch') return preview.patches[index]?.error ?? null;
  if (kind === 'tab') return preview.tabs[index]?.error ?? null;
  return null;
}

function renderBoard(into: HTMLElement, { spec, selection, commit }: InspectorInput): void {
  const index = selection!.index;
  const board = spec.boards?.[index];
  if (board === undefined) return;
  const edit = (change: (b: NonNullable<typeof board>) => void): void =>
    commit((s) => {
      const target = s.boards?.[index];
      if (target) change(target);
    });

  const source = board.source;
  const describeSource =
    source === undefined ? 'auto'
    : source.type === 'annotation' ? `annotation ${source.ref}`
    : source.type === 'rectangle' ? `rectangle ${source.tl.join(', ')} → ${source.br.join(', ')}`
    : 'auto';

  into.append(
    field('name', textInput(`b${index}.name`, board.name, `board_${index}`, (v) => edit((b) => {
      if (v === undefined) delete b.name; else b.name = v;
    }))),
    readOnly('file', board.file),
    readOnly('source', describeSource),
    field('position', vec2Input(`b${index}.position`, board.position, (v) => edit((b) => {
      if (v !== undefined) b.position = v;
    }))),
    field('anchor', selectInput(`b${index}.anchor`, board.anchor, ANCHORS, 'center', (v) =>
      edit((b) => { b.anchor = v as Anchor; }))),
    field('rotation', numberInput(`b${index}.rotation`, board.rotation, '0', (v) => edit((b) => {
      if (v === undefined) delete b.rotation; else b.rotation = v;
    }), '45')),
    field('tolerance', numberInput(`b${index}.tolerance`, board.tolerance,
      String(spec.defaults?.tolerance ?? 1), (v) => edit((b) => {
        if (v === undefined) delete b.tolerance; else b.tolerance = v;
      }))),
  );
}

function renderTab(into: HTMLElement, { spec, selection, commit }: InspectorInput): void {
  const index = selection!.index;
  const tab = spec.tabs?.[index];
  if (tab === undefined) return;
  const edit = (change: (t: NonNullable<typeof tab>) => void): void =>
    commit((s) => {
      const target = s.tabs?.[index];
      if (target) change(target);
    });

  into.append(
    field('origin', vec2Input(`t${index}.origin`, tab.origin, (v) => edit((t) => {
      if (v !== undefined) t.origin = v;
    }))),
    field('direction', vec2Input(`t${index}.direction`, tab.direction, (v) => edit((t) => {
      if (v !== undefined) t.direction = v;
    }))),
    field('width', numberInput(`t${index}.width`, tab.width,
      String(spec.defaults?.tab_width ?? 3), (v) => edit((t) => {
        if (v === undefined) delete t.width; else t.width = v;
      }))),
    field('sides', selectInput(`t${index}.sides`, tab.sides, ['both', 'one'], 'both', (v) =>
      edit((t) => { t.sides = v as 'both' | 'one'; }))),
    field('max_length', numberInput(`t${index}.max`, tab.max_length,
      String(spec.defaults?.tab_max_length ?? 50), (v) => edit((t) => {
        if (v === undefined) delete t.max_length; else t.max_length = v;
      }))),
  );
}

function renderRail(into: HTMLElement, { selection, spec, commit }: InspectorInput): void {
  const index = selection!.index;
  const rail = spec.rails?.[index];
  if (rail === undefined) return;
  const edit = (change: (r: NonNullable<typeof rail>) => void): void =>
    commit((s) => {
      const target = s.rails?.[index];
      if (target) change(target);
    });

  into.append(readOnly('type', rail.type));

  if (rail.type === 'rect') {
    into.append(
      field('at', vec2Input(`r${index}.at`, rail.at, (v) => edit((r) => { if (v) r.at = v; }))),
      field('size', vec2Input(`r${index}.size`, rail.size, (v) => edit((r) => { if (v) r.size = v; }))),
    );
  } else if (rail.type === 'poly') {
    const area = h('textarea', { class: 'rawyaml', 'data-fkey': `r${index}.points` },
      JSON.stringify(rail.points ?? []));
    area.addEventListener('change', () => {
      try {
        const parsed = JSON.parse(area.value) as Vec2[];
        if (Array.isArray(parsed)) edit((r) => { r.points = parsed; });
      } catch {
        area.classList.add('bad');
      }
    });
    into.append(field('points', area));
  } else if (rail.type === 'frame') {
    into.append(
      field('width', numberInput(`r${index}.w`, rail.width, '5', (v) => edit((r) => {
        if (v === undefined) delete r.width; else r.width = v;
      }))),
      field('hspace', numberInput(`r${index}.hs`, rail.hspace, '3', (v) => edit((r) => {
        if (v === undefined) delete r.hspace; else r.hspace = v;
      }))),
      field('vspace', numberInput(`r${index}.vs`, rail.vspace, '3', (v) => edit((r) => {
        if (v === undefined) delete r.vspace; else r.vspace = v;
      }))),
      field('corner_cuts', selectInput(`r${index}.cc`, rail.corner_cuts,
        ['none', 'both', 'v', 'h'], 'none', (v) => edit((r) => {
          r.corner_cuts = v as 'none' | 'both' | 'v' | 'h';
        }))),
    );
  } else {
    into.append(
      field('thickness', numberInput(`r${index}.t`, rail.thickness, '5', (v) => edit((r) => {
        if (v === undefined) delete r.thickness; else r.thickness = v;
      }))),
      field('space', numberInput(`r${index}.s`, rail.space, '0', (v) => edit((r) => {
        if (v === undefined) delete r.space; else r.space = v;
      }))),
      field('span', vec2Input(`r${index}.span`, rail.span, (v) => edit((r) => {
        if (v === undefined) delete r.span; else r.span = v;
      }))),
    );
  }
}

function renderText(into: HTMLElement, { spec, selection, commit }: InspectorInput): void {
  const index = selection!.index;
  const item = spec.text?.[index];
  if (item === undefined) return;
  const edit = (change: (t: NonNullable<typeof item>) => void): void =>
    commit((s) => {
      const target = s.text?.[index];
      if (target) change(target);
    });

  into.append(
    field('text', textInput(`x${index}.text`, item.text, '', (v) => edit((t) => { t.text = v ?? ''; }))),
    field('at', vec2Input(`x${index}.at`, item.at, (v) => edit((t) => { if (v) t.at = v; }))),
    field('layer', textInput(`x${index}.layer`, item.layer, 'F.SilkS', (v) => edit((t) => {
      if (v === undefined) delete t.layer; else t.layer = v;
    }))),
    field('height', numberInput(`x${index}.h`, item.height, '1.5', (v) => edit((t) => {
      if (v === undefined) delete t.height; else t.height = v;
    }))),
    field('thickness', numberInput(`x${index}.t`, item.thickness, '0.3', (v) => edit((t) => {
      if (v === undefined) delete t.thickness; else t.thickness = v;
    }))),
    field('rotation', numberInput(`x${index}.r`, item.rotation, '0', (v) => edit((t) => {
      if (v === undefined) delete t.rotation; else t.rotation = v;
    }), '45')),
  );
}

// --- panel settings -----------------------------------------------------------------

export function renderSettings(into: HTMLElement, spec: PanelSpec, commit: Commit): void {
  clear(into);

  into.append(field('output', textInput('output', spec.output, 'panel.kicad_pcb', (v) =>
    commit((s) => { s.output = v ?? 'panel.kicad_pcb'; }))));

  // cuts
  into.append(subHead('cuts'));
  const cutsType = spec.cuts?.type ?? 'mousebites';
  into.append(field('type', selectInput('cuts.type', cutsType,
    ['mousebites', 'vcuts', 'layer', 'none'], 'mousebites', (v) =>
      commit((s) => { s.cuts = { ...(s.cuts ?? {}), type: v as never }; }))));

  const editCuts = (key: string, value: number | undefined): void =>
    commit((s) => {
      s.cuts = { ...(s.cuts ?? {}) };
      if (value === undefined) delete (s.cuts as Record<string, unknown>)[key];
      else (s.cuts as Record<string, unknown>)[key] = value;
    });

  if (cutsType === 'mousebites') {
    into.append(
      field('drill', numberInput('cuts.drill', spec.cuts?.drill, '0.5', (v) => editCuts('drill', v))),
      field('spacing', numberInput('cuts.spacing', spec.cuts?.spacing, '0.8', (v) => editCuts('spacing', v))),
      field('offset', numberInput('cuts.offset', spec.cuts?.offset, '0.25', (v) => editCuts('offset', v))),
      field('prolong', numberInput('cuts.prolong', spec.cuts?.prolong, '0.5', (v) => editCuts('prolong', v))),
    );
  } else if (cutsType === 'vcuts') {
    into.append(checkboxInput('cuts.curves', spec.cuts?.cut_curves, false, 'cut_curves', (v) =>
      commit((s) => { s.cuts = { ...(s.cuts ?? {}), cut_curves: v }; })));
  } else if (cutsType === 'layer') {
    into.append(field('layer', textInput('cuts.layer', spec.cuts?.layer, 'Cmts.User', (v) =>
      commit((s) => { s.cuts = { ...(s.cuts ?? {}), layer: v ?? 'Cmts.User' }; }))));
  }

  // defaults
  into.append(subHead('defaults'));
  const editDefaults = (key: string, value: unknown): void =>
    commit((s) => {
      s.defaults = { ...(s.defaults ?? {}) };
      if (value === undefined) delete (s.defaults as Record<string, unknown>)[key];
      else (s.defaults as Record<string, unknown>)[key] = value;
    });
  into.append(
    field('tolerance', numberInput('def.tol', spec.defaults?.tolerance, '1', (v) => editDefaults('tolerance', v))),
    field('tab_width', numberInput('def.tw', spec.defaults?.tab_width, '3', (v) => editDefaults('tab_width', v))),
    field('tab_max_length', numberInput('def.tml', spec.defaults?.tab_max_length, '50', (v) => editDefaults('tab_max_length', v))),
    field('net_rename', textInput('def.nr', spec.defaults?.net_rename, 'Board_{n}-{orig}', (v) => editDefaults('net_rename', v))),
    field('ref_rename', textInput('def.rr', spec.defaults?.ref_rename, '{orig}', (v) => editDefaults('ref_rename', v))),
  );

  // post
  into.append(subHead('post'));
  const editPost = (key: string, value: unknown): void =>
    commit((s) => {
      s.post = { ...(s.post ?? {}) };
      if (value === undefined) delete (s.post as Record<string, unknown>)[key];
      else (s.post as Record<string, unknown>)[key] = value;
    });
  into.append(
    field('mill_radius', numberInput('post.mr', spec.post?.mill_radius, '0', (v) => editPost('mill_radius', v))),
    checkboxInput('post.ra', spec.post?.reconstruct_arcs, true, 'reconstruct_arcs', (v) => editPost('reconstruct_arcs', v)),
    checkboxInput('post.rz', spec.post?.refill_zones, false, 'refill_zones', (v) => editPost('refill_zones', v)),
    checkboxInput('post.so', spec.post?.set_origin, true, 'set_origin', (v) => editPost('set_origin', v)),
  );

  // page
  into.append(subHead('page'));
  const pageSize = spec.page?.size;
  into.append(
    field('size', textInput('page.size', Array.isArray(pageSize) ? pageSize.join(', ') : pageSize,
      'A4 or 200, 150', (v) => commit((s) => {
        s.page = { ...(s.page ?? {}) };
        if (v === undefined) { delete s.page.size; return; }
        // "200, 150" is a custom sheet; anything else is a named size.
        const parts = v.split(',').map((p) => Number(p.trim()));
        s.page.size = parts.length === 2 && parts.every((n) => Number.isFinite(n))
          ? [parts[0]!, parts[1]!]
          : v;
      }))),
    field('position', vec2Input('page.pos', spec.page?.position, (v) => commit((s) => {
      s.page = { ...(s.page ?? {}) };
      if (v === undefined) delete s.page.position; else s.page.position = v;
    }))),
  );

  optionalSection(into, spec, commit, 'tooling',
    { count: 4, hoffset: 2.5, voffset: 2.5, diameter: 1.5 });
  optionalSection(into, spec, commit, 'fiducials',
    { count: 3, hoffset: 5, voffset: 2.5, copper: 1, opening: 2 });
  optionalSection(into, spec, commit, 'copper_fill', { clearance: 1 });

  yamlSection(into, spec, commit, 'patches');
  yamlSection(into, spec, commit, 'manual_cuts');
}

/** A block that is either absent or present in full, toggled by a checkbox. */
function optionalSection(
  into: HTMLElement,
  spec: PanelSpec,
  commit: Commit,
  key: 'tooling' | 'fiducials' | 'copper_fill',
  defaults: Record<string, number>,
): void {
  const present = spec[key] !== undefined && spec[key] !== null && spec[key] !== false;
  const toggle = h('input', { type: 'checkbox', 'data-fkey': `opt.${key}` });
  toggle.checked = present;
  toggle.addEventListener('change', () =>
    commit((s) => {
      if (toggle.checked) (s as Record<string, unknown>)[key] = { ...defaults };
      else delete (s as Record<string, unknown>)[key];
    }));

  into.append(subHead(key, toggle));
  if (!present) return;

  const current = (spec[key] ?? {}) as Record<string, number>;
  for (const name of Object.keys(defaults)) {
    into.append(field(name, numberInput(`${key}.${name}`, current[name], String(defaults[name]),
      (v) => commit((s) => {
        const block = { ...((s as Record<string, unknown>)[key] as Record<string, unknown>) };
        if (v === undefined) delete block[name]; else block[name] = v;
        (s as Record<string, unknown>)[key] = block;
      }))));
  }
}

/**
 * A raw-YAML editor for the sections with no dedicated widgets.
 *
 * `patches` and `manual_cuts` are rare and free-form enough that a form would be
 * more cage than help, and this keeps every part of the grammar reachable.
 */
function yamlSection(
  into: HTMLElement,
  spec: PanelSpec,
  commit: Commit,
  key: 'patches' | 'manual_cuts',
): void {
  into.append(subHead(`${key} (yaml)`));
  const area = h('textarea', { class: 'rawyaml', 'data-fkey': `yaml.${key}` },
    emitYamlFragment(spec[key] ?? []));
  const error = h('div', { class: 'insp-error' });
  error.style.display = 'none';

  area.addEventListener('change', () => {
    try {
      const value = parseYaml(area.value);
      error.style.display = 'none';
      commit((s) => {
        if (value === null || (Array.isArray(value) && value.length === 0)) {
          delete (s as Record<string, unknown>)[key];
        } else {
          (s as Record<string, unknown>)[key] = value;
        }
      });
    } catch (e) {
      error.textContent = e instanceof YamlError ? e.message : String(e);
      error.style.display = '';
    }
  });

  into.append(area, error);
}
